// Platform Admin — Tenant 360 drawer (ADMIN_UPGRADE_PLAN Phase 1).
// Slide-over panel: Overview (profile, onboarding funnel, health, usage,
// quota overrides, credits), Members, Subscribers, Activity (audit).
import { useCallback, useEffect, useState } from 'react';
import { cn } from '../../lib/cn';
import { useI18n } from '../../lib/i18n';
import { useConfirm } from '../ui/ConfirmDialog';
import {
  getTenant, updateTenant, setOverrides, grantCredits,
  listMembers, setMemberRole, removeMember,
  inviteMember, listInvites, revokeInvite,
  listSubscribers, upsertSubscriber, deleteSubscriber, listAudit,
  type TenantDetail, type AdminMember, type AdminSubscriber, type AuditEntry,
  type AdminInvite, type MemberRole, type QuotaOverrides,
} from '../../lib/padminTenant';
import { listApiKeys, revokeApiKey, type AdminApiKey } from '../../lib/padminOps';

const FLD = 'w-full rounded-[8px] border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-primary';
const BTN = 'rounded-[8px] px-3 py-1.5 text-xs font-bold disabled:opacity-40';
const TABS = ['overview', 'members', 'subscribers', 'keys', 'activity'] as const;
type Tab = (typeof TABS)[number];

/** Promise-based single-line text prompt (replaces native window.prompt). */
interface TextPromptReq {
  title: string;
  minLength: number;
  resolve: (value: string | null) => void;
}

function TextPromptModal({ req, onDone }: { req: TextPromptReq; onDone: (value: string | null) => void }) {
  const { t } = useI18n();
  const [value, setValue] = useState('');
  const ok = value.trim().length >= req.minLength;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="presentation">
      <button type="button" aria-label={t('common.cancel')} onClick={() => onDone(null)} className="absolute inset-0 bg-slate-900/40" />
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="pa-tenant-text-prompt-label"
        onSubmit={(e) => { e.preventDefault(); if (ok) onDone(value.trim()); }}
        className="relative z-10 w-full max-w-sm rounded-card border border-slate-200 bg-white p-5 shadow-lg"
      >
        <label id="pa-tenant-text-prompt-label" htmlFor="pa-tenant-text-prompt" className="block text-sm font-bold text-ink">{req.title}</label>
        <input
          id="pa-tenant-text-prompt"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="mt-3 w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => onDone(null)} className="rounded-lg border border-slate-200 px-3.5 py-2 text-xs font-bold text-ink-2 hover:bg-surface">{t('common.cancel')}</button>
          <button type="submit" disabled={!ok} className="rounded-lg bg-primary px-3.5 py-2 text-xs font-bold text-white hover:bg-primary/90 disabled:opacity-40">{t('common.confirm')}</button>
        </div>
      </form>
    </div>
  );
}

function Step({ done, label }: { done: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className={cn('grid h-5 w-5 flex-shrink-0 place-items-center rounded-full text-[11px] font-bold',
        done ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-ink-3')}>{done ? '✓' : '•'}</span>
      <span className={cn('text-xs', done ? 'font-semibold text-ink' : 'text-ink-3')}>{label}</span>
    </div>
  );
}

function Bar({ used, limit }: { used: number; limit: number | undefined }) {
  // A hand-seeded plans/{id} doc may lack a quota key; never crash the drawer over it.
  const lim = Number.isFinite(limit) ? (limit as number) : 0;
  const pct = lim > 0 ? Math.min(100, Math.round((used / lim) * 100)) : 0;
  return (
    <div>
      <div className="text-[11px] text-ink-2">{(used ?? 0).toLocaleString()}/{lim.toLocaleString()}</div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-200">
        <i className={cn('block h-full rounded-full', pct >= 90 ? 'bg-red-500' : 'bg-primary')} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function TenantDrawer({ tenantId, onClose, onChanged }: {
  tenantId: string; onClose: () => void; onChanged: () => void;
}) {
  const { t: tr } = useI18n();
  const confirm = useConfirm();
  const [textReq, setTextReq] = useState<TextPromptReq | null>(null);
  const askText = (title: string, minLength = 1) =>
    new Promise<string | null>((resolve) => setTextReq({ title, minLength, resolve }));

  const [tab, setTab] = useState<Tab>('overview');
  const [d, setD] = useState<TenantDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // editable copies
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState('');
  const [ov, setOv] = useState<QuotaOverrides>({});
  const [bonus, setBonus] = useState('');
  const [freeDays, setFreeDays] = useState('');
  const [reason, setReason] = useState('');

  const [members, setMembers] = useState<AdminMember[] | null>(null);
  const [invites, setInvites] = useState<AdminInvite[] | null>(null);
  const [invEmail, setInvEmail] = useState('');
  const [invRole, setInvRole] = useState<'agent' | 'admin'>('agent');
  const [subs, setSubs] = useState<AdminSubscriber[] | null>(null);
  const [subQ, setSubQ] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newName, setNewName] = useState('');
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [keys, setKeys] = useState<AdminApiKey[] | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await getTenant(tenantId);
      setD(r); setName(r.tenant.name); setNotes(r.tenant.notes);
      setTags(r.tenant.tags.join(', ')); setOv(r.tenant.overrides ?? {});
    } catch (e) { setErr((e as Error).message); }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  // Escape closes the drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => { if (tab === 'members' && !members) void listMembers(tenantId).then(setMembers).catch((e) => setErr(e.message)); }, [tab, members, tenantId]);
  useEffect(() => { if (tab === 'members' && !invites) void listInvites(tenantId).then(setInvites).catch(() => setInvites([])); }, [tab, invites, tenantId]);
  useEffect(() => { if (tab === 'subscribers' && !subs) void listSubscribers(tenantId).then(setSubs).catch((e) => setErr(e.message)); }, [tab, subs, tenantId]);
  useEffect(() => { if (tab === 'activity' && !audit) void listAudit(tenantId, 50).then(setAudit).catch((e) => setErr(e.message)); }, [tab, audit, tenantId]);
  useEffect(() => { if (tab === 'keys' && !keys) void listApiKeys(tenantId).then(setKeys).catch((e) => setErr(e.message)); }, [tab, keys, tenantId]);

  async function run(fn: () => Promise<unknown>, after?: () => void) {
    setBusy(true); setErr(null);
    try { await fn(); after?.(); await load(); onChanged(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  const t = d?.tenant;

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        type="button"
        aria-label={tr('common.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/30"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t?.name ?? tenantId}
        className="relative z-10 flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-surface shadow-2xl"
      >
        <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-3">
          <div className="min-w-0">
            <b className="block truncate text-base text-ink">{t?.name ?? '…'}</b>
            <span className="font-mono text-[11px] text-ink-3">{tenantId}</span>
          </div>
          <div className="flex-1" />
          <button type="button" onClick={onClose} aria-label={tr('common.close')} className="rounded-full px-2 py-1 text-lg text-ink-3 hover:bg-surface">✕</button>
        </header>

        <nav className="flex gap-1 border-b border-slate-200 bg-white px-5 overflow-x-auto">
          {TABS.map((x) => (
            <button key={x} type="button" onClick={() => setTab(x)}
              className={cn('-mb-px border-b-2 px-3 py-2.5 text-sm font-bold',
                tab === x ? 'border-ink text-ink' : 'border-transparent text-ink-3 hover:text-ink')}>{tr(`pa.tenant.tab.${x}`)}</button>
          ))}
        </nav>

        <div className="space-y-4 p-5">
          {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
          {!d && !err && <div className="py-10 text-center text-sm text-ink-3">…</div>}

          {d && tab === 'overview' && (
            <>
              {/* Why is this tenant suspended? Support should never have to guess. */}
              {t?.status === 'suspended' && (
                <div className="rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm">
                  <b className="text-red-700">{tr('pa.tenant.suspended')}</b>
                  <span className="ms-2 text-red-800">{t.suspendReason || tr('pa.tenant.noReason')}</span>
                </div>
              )}

              {/* Onboarding funnel */}
              <div className="rounded-card border border-slate-200 bg-white p-4 shadow-sm">
                <b className="block text-sm text-ink">{tr('pa.tenant.onboarding')}</b>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <Step done label={tr('pa.tenant.step.signedUp')} />
                  <Step done={d.onboarding.connected} label={tr('pa.tenant.step.connected')} />
                  <Step done={d.onboarding.templateApproved} label={tr('pa.tenant.step.templateApproved')} />
                  <Step done={d.onboarding.firstBroadcast} label={tr('pa.tenant.step.firstBroadcast')} />
                </div>
                <p className="mt-2 text-[11px] text-ink-3">
                  {d.health.verifiedPhoneE164 ?? tr('pa.tenant.noNumber')} · {d.health.isLive ? tr('pa.tenant.live') : tr('pa.tenant.sandbox')} · {tr('pa.tenant.plan', { plan: t!.planId })} · {t!.status}
                </p>
              </div>

              {/* Usage vs effective quota */}
              <div className="rounded-card border border-slate-200 bg-white p-4 shadow-sm">
                <b className="block text-sm text-ink">{tr('pa.tenant.usage', { month: d.usage.month })}</b>
                <div className="mt-3 grid gap-4 sm:grid-cols-3">
                  <div><span className="text-[11px] text-ink-3">{tr('pa.t.col.subs')}</span><Bar used={d.usage.subscribers} limit={t!.quotas.subscribers} /></div>
                  <div><span className="text-[11px] text-ink-3">{tr('pa.t.col.msgs')}</span><Bar used={d.usage.messages} limit={t!.quotas.messagesPerMonth} /></div>
                  <div><span className="text-[11px] text-ink-3">{tr('pa.tenant.aiTokens')}</span><Bar used={d.usage.aiTokens} limit={t!.quotas.aiTokensPerMonth} /></div>
                </div>
                {t!.credits.bonusMessages > 0 && <p className="mt-2 text-[11px] font-semibold text-green-700">{tr('pa.tenant.bonusMessages', { n: t!.credits.bonusMessages.toLocaleString() })}</p>}
                {t!.credits.freeUntil && <p className="text-[11px] font-semibold text-green-700">{tr('pa.tenant.freeUntil', { date: new Date(t!.credits.freeUntil).toLocaleDateString() })}</p>}
              </div>

              {/* Profile */}
              <div className="rounded-card border border-slate-200 bg-white p-4 shadow-sm space-y-2">
                <b className="block text-sm text-ink">{tr('pa.tenant.profile')}</b>
                <label className="block"><span className="text-[11px] text-ink-3">{tr('subs.col.name')}</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} className={FLD} /></label>
                <label className="block"><span className="text-[11px] text-ink-3">{tr('pa.tenant.tags')}</span>
                  <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder={tr('pa.tenant.tagsPlaceholder')} className={FLD} /></label>
                <label className="block"><span className="text-[11px] text-ink-3">{tr('pa.tenant.notes')}</span>
                  <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={FLD} /></label>
                <button type="button" disabled={busy} className={cn(BTN, 'bg-primary text-white')}
                  onClick={() => void run(() => updateTenant({
                    tenantId, name, notes,
                    tags: tags.split(',').map((s) => s.trim()).filter(Boolean),
                  }))}>{tr('pa.tenant.saveProfile')}</button>
                {d.owner && <p className="text-[11px] text-ink-3">{tr('pa.tenant.owner', { owner: d.owner.email ?? d.owner.uid })}</p>}
              </div>

              {/* Quota overrides */}
              <div className="rounded-card border border-slate-200 bg-white p-4 shadow-sm space-y-2">
                <b className="block text-sm text-ink">{tr('pa.tenant.overrides')}</b>
                <p className="text-[11px] text-ink-3">{tr('pa.tenant.overridesNote')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {([['subscribers', 'pa.t.col.subs'], ['messagesPerMonth', 'pa.tenant.messagesPerMonth'], ['aiTokensPerMonth', 'pa.tenant.aiTokensPerMonth'], ['teamSeats', 'pa.plan.seats']] as const).map(([k, lbl]) => (
                    <label key={k} className="block"><span className="text-[11px] text-ink-3">{tr(lbl)}</span>
                      <input type="number" value={ov[k] ?? ''} onChange={(e) => setOv({ ...ov, [k]: e.target.value === '' ? undefined : Number(e.target.value) })} className={FLD} /></label>
                  ))}
                </div>
                <button type="button" disabled={busy} className={cn(BTN, 'bg-primary text-white')}
                  onClick={() => void run(() => setOverrides(tenantId, ov))}>{tr('pa.tenant.applyOverrides')}</button>
              </div>

              {/* Credits */}
              <div className="rounded-card border border-slate-200 bg-white p-4 shadow-sm space-y-2">
                <b className="block text-sm text-ink">{tr('pa.tenant.grantCredits')}</b>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="block"><span className="text-[11px] text-ink-3">{tr('pa.tenant.bonus')}</span>
                    <input type="number" value={bonus} onChange={(e) => setBonus(e.target.value)} className={FLD} /></label>
                  <label className="block"><span className="text-[11px] text-ink-3">{tr('pa.tenant.freeDays')}</span>
                    <input type="number" value={freeDays} onChange={(e) => setFreeDays(e.target.value)} className={FLD} /></label>
                </div>
                <label className="block"><span className="text-[11px] text-ink-3">{tr('pa.tenant.reason')}</span>
                  <input value={reason} onChange={(e) => setReason(e.target.value)} className={FLD} /></label>
                <button type="button" disabled={busy || reason.trim().length < 3} className={cn(BTN, 'bg-primary text-white')}
                  onClick={() => void run(
                    () => grantCredits({
                      tenantId,
                      bonusMessages: bonus ? Number(bonus) : undefined,
                      freeDays: freeDays ? Number(freeDays) : undefined,
                      reason: reason.trim(),
                    }),
                    () => { setBonus(''); setFreeDays(''); setReason(''); },
                  )}>{tr('pa.tenant.grant')}</button>
              </div>
            </>
          )}

          {d && tab === 'members' && (
            <div className="space-y-3">
            {/* Invite on the tenant's behalf — support flow when the owner is locked out. */}
            <div className="rounded-card border border-slate-200 bg-white p-4 shadow-sm">
              <b className="block text-sm text-ink">{tr('pa.mem.invite')}</b>
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <label className="w-full sm:min-w-[200px] sm:flex-1">
                  <span className="mb-1 block text-[11px] font-bold text-ink-2">{tr('pa.mem.inviteEmail')}</span>
                  <input type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)}
                    placeholder="name@company.com" className={FLD} />
                </label>
                <label>
                  <span className="mb-1 block text-[11px] font-bold text-ink-2">{tr('pa.mem.inviteRole')}</span>
                  <select value={invRole} onChange={(e) => setInvRole(e.target.value as 'agent' | 'admin')}
                    className="rounded-[8px] border border-slate-200 px-2 py-1.5 text-sm font-bold">
                    <option value="agent">{tr('roles.agent')}</option><option value="admin">{tr('roles.admin')}</option>
                  </select>
                </label>
                <button type="button" disabled={busy || !invEmail.includes('@')}
                  className={cn(BTN, 'bg-primary text-white')}
                  onClick={() => void run(
                    () => inviteMember({ tenantId, email: invEmail.trim(), role: invRole }),
                    () => { setInvEmail(''); setInvites(null); },
                  )}>{tr('pa.mem.inviteSend')}</button>
              </div>

              {invites && invites.filter((i) => i.status === 'pending').length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <span className="block text-[11px] font-bold uppercase tracking-wide text-ink-3">{tr('pa.mem.pending')}</span>
                  {invites.filter((i) => i.status === 'pending').map((i) => (
                    <div key={i.id} className="flex items-center gap-2 py-1.5">
                      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{i.email}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-ink-2">{tr(`roles.${i.role}`)}</span>
                      <button type="button" disabled={busy} className={cn(BTN, 'border border-slate-200 text-ink-2')}
                        onClick={() => void run(() => revokeInvite(tenantId, i.id), () => setInvites(null))}>{tr('pa.mem.revoke')}</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-card border border-slate-200 bg-white p-4 shadow-sm">
              {!members ? <div className="py-6 text-center text-sm text-ink-3">…</div> : members.length === 0 ? (
                <p className="text-sm text-ink-3">{tr('pa.tenant.noMembers')}</p>
              ) : members.map((m) => (
                <div key={m.uid} className="flex flex-wrap items-center gap-2 border-b border-slate-100 py-2 last:border-0">
                  <div className="min-w-0 flex-1">
                    <b className="block truncate text-sm text-ink">{m.displayName || m.email || m.uid}</b>
                    <span className="text-[11px] text-ink-3">{m.email} · {tr('pa.tenant.lastSeen', { date: m.lastSignIn ? new Date(m.lastSignIn).toLocaleDateString() : '—' })}</span>
                  </div>
                  <select value={m.role} disabled={busy}
                    onChange={(e) => void run(() => setMemberRole(tenantId, m.uid, e.target.value as MemberRole), () => setMembers(null))}
                    className="rounded-[8px] border border-slate-200 px-2 py-1 text-xs font-bold">
                    <option value="owner">{tr('roles.owner')}</option><option value="admin">{tr('roles.admin')}</option><option value="agent">{tr('roles.agent')}</option>
                  </select>
                  {m.role !== 'owner' && (
                    <button type="button" disabled={busy} className={cn(BTN, 'border border-red-500 text-red-500')}
                      onClick={() => {
                        void (async () => {
                          const ok = await confirm({
                            title: tr('pa.tenant.removeMemberConfirm', { who: m.email ?? m.uid }),
                            confirmLabel: tr('common.remove'),
                            danger: true,
                          });
                          if (ok) void run(() => removeMember(tenantId, m.uid), () => setMembers(null));
                        })();
                      }}>{tr('common.remove')}</button>
                  )}
                </div>
              ))}
            </div>
            </div>
          )}

          {d && tab === 'keys' && (
            <div className="rounded-card border border-slate-200 bg-white p-4 shadow-sm">
              <b className="block text-sm text-ink">{tr('pa.tenant.apiKeys')}</b>
              {!keys ? <div className="py-6 text-center text-sm text-ink-3">…</div> : keys.length === 0 ? (
                <p className="mt-2 text-sm text-ink-3">{tr('pa.tenant.noApiKeys')}</p>
              ) : keys.map((k) => (
                <div key={k.id} className="flex flex-wrap items-center gap-2 border-b border-slate-100 py-2 last:border-0">
                  <div className="min-w-0 flex-1">
                    <b className="block truncate text-sm text-ink">{k.label || k.id}</b>
                    <span className="font-mono text-[11px] text-ink-3">
                      {k.prefix}…{k.last4} · {tr('pa.tenant.lastUsed', { date: k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : '—' })}
                    </span>
                  </div>
                  {k.revoked ? (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-ink-3">{tr('pa.tenant.revoked')}</span>
                  ) : (
                    <button type="button" disabled={busy} className={cn(BTN, 'border border-red-500 text-red-500')}
                      onClick={() => {
                        void (async () => {
                          const why = await askText(tr('pa.tenant.revokeKeyPrompt'), 3);
                          if (!why) return;
                          void run(() => revokeApiKey({ tenantId, keyId: k.id, reason: why }), () => setKeys(null));
                        })();
                      }}>{tr('pa.mem.revoke')}</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {d && tab === 'subscribers' && (
            <div className="space-y-3">
              <div className="rounded-card border border-slate-200 bg-white p-4 shadow-sm space-y-2">
                <b className="block text-sm text-ink">{tr('pa.tenant.addContact')}</b>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="+96890000000" aria-label={tr('subs.col.phone') || 'Phone'} className={FLD} />
                  <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={tr('subs.col.name')} aria-label={tr('subs.col.name') || 'Name'} className={FLD} />
                </div>
                <button type="button" disabled={busy || !/^\+[1-9]\d{6,14}$/.test(newPhone.trim())} className={cn(BTN, 'bg-primary text-white')}
                  onClick={() => void run(
                    () => upsertSubscriber({ tenantId, phoneE164: newPhone.trim(), name: newName.trim() || undefined }),
                    () => { setNewPhone(''); setNewName(''); setSubs(null); },
                  )}>{tr('pa.tenant.addContactBtn')}</button>
              </div>
              <input value={subQ} onChange={(e) => setSubQ(e.target.value)} placeholder={tr('pa.tenant.searchSubs')} className={FLD} />
              <div className="rounded-card border border-slate-200 bg-white p-4 shadow-sm">
                {!subs ? <div className="py-6 text-center text-sm text-ink-3">…</div> : (
                  subs.filter((s) => !subQ || s.phoneE164.includes(subQ) || s.name.toLowerCase().includes(subQ.toLowerCase())).map((s) => (
                    <div key={s.id} className="flex items-center gap-2 border-b border-slate-100 py-2 last:border-0">
                      <div className="min-w-0 flex-1">
                        <b className="block truncate text-sm text-ink">{s.name || s.phoneE164}</b>
                        <span className="font-mono text-[11px] text-ink-3">{s.phoneE164}{s.labels.length ? ` · ${s.labels.join(', ')}` : ''}</span>
                      </div>
                      {/* Compliance state must be visible: a STOP'd contact is never messageable. */}
                      {s.optedOutAt ? (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700" title={tr('pa.tenant.stopOn', { date: new Date(s.optedOutAt).toLocaleDateString() })}>{tr('subs.status.unsubscribed')}</span>
                      ) : s.optedIn ? (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-700">{s.marketingOptIn ? tr('pa.tenant.marketingOk') : tr('subs.status.subscribed')}</span>
                      ) : (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-ink-3">{tr('subs.status.pending')}</span>
                      )}
                      <button type="button" disabled={busy} className={cn(BTN, 'border border-red-500 text-red-500')}
                        onClick={() => {
                          void (async () => {
                            const ok = await confirm(s.optedOutAt
                              ? {
                                  title: tr('pa.tenant.hide'),
                                  body: tr('pa.tenant.hideStopConfirm', { phone: s.phoneE164 }),
                                  confirmLabel: tr('pa.tenant.hide'),
                                  danger: true,
                                }
                              : {
                                  title: tr('pa.tenant.deleteSubConfirm', { phone: s.phoneE164 }),
                                  body: tr('common.cannotUndo'),
                                  danger: true,
                                });
                            if (ok) void run(() => deleteSubscriber(tenantId, s.id), () => setSubs(null));
                          })();
                        }}>{s.optedOutAt ? tr('pa.tenant.hide') : tr('common.delete')}</button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {d && tab === 'activity' && (
            <div className="rounded-card border border-slate-200 bg-white p-4 shadow-sm">
              {!audit ? <div className="py-6 text-center text-sm text-ink-3">…</div> : audit.length === 0 ? (
                <p className="text-sm text-ink-3">{tr('pa.tenant.noActivity')}</p>
              ) : audit.map((a) => (
                <div key={a.id} className="border-b border-slate-100 py-2 text-xs last:border-0">
                  <b className="font-mono text-ink">{a.action}</b>
                  <span className="text-ink-3"> · {new Date(a.at).toLocaleString()} · {a.actorEmail ?? tr('pa.tenant.actorAdmin')}</span>
                  {a.reason && <div className="text-ink-2">“{a.reason}”</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
      {textReq && (
        <TextPromptModal req={textReq} onDone={(v) => { setTextReq(null); textReq.resolve(v); }} />
      )}
    </div>
  );
}
